import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { tradingService } from '../../services/trading';
import { BottomSheet } from '../ui/BottomSheet';

interface Trade {
  id: string;
  ticker: string;
  symbolDescription?: string;
  action: 'BUY' | 'SELL';
  units: string;
  price?: string;
  executedPrice?: string;
  grossAmount?: string;
  commission?: string;
  netAmount?: string;
  total?: string;
  status: string;
  rejected: boolean;
  rejectionMessage?: string;
  executedAt?: string;
  createdAt: string;
  algorithm?: string;
  snapTradeOrderId?: string;
}

interface Filters {
  ticker: string;
  algorithm: string;
  timeRange: 'today' | 'week' | 'month' | 'year' | 'all';
}

const ITEMS_PER_PAGE = 10;

interface EnhancedTradeHistoryProps {
  filteredTrades?: any[];
  isLoading?: boolean;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  itemsPerPage?: number;
}

export function EnhancedTradeHistory({
  filteredTrades: propFilteredTrades,
  isLoading: propIsLoading,
  currentPage: propCurrentPage,
  setCurrentPage: propSetCurrentPage,
  itemsPerPage: propItemsPerPage
}: EnhancedTradeHistoryProps = {}) {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    ticker: '',
    algorithm: '',
    timeRange: 'all',
  });
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  // Use provided or internal pagination state
  const currentPage = propCurrentPage !== undefined ? propCurrentPage : internalCurrentPage;
  const setCurrentPage = propSetCurrentPage || setInternalCurrentPage;
  const itemsPerPage = propItemsPerPage || ITEMS_PER_PAGE;

  // Fetch trade history ONLY if not provided via props
  const { data: fetchedTrades = [], isLoading: fetchIsLoading, error } = useQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
    enabled: propFilteredTrades === undefined, // Only fetch if not provided
  });

  // Use provided trades or fetched trades
  const trades = propFilteredTrades !== undefined ? propFilteredTrades : fetchedTrades;
  const isLoading = propIsLoading !== undefined ? propIsLoading : fetchIsLoading;

  // Get unique tickers and algorithms for filter dropdowns
  const uniqueTickers = useMemo(() => {
    const tickers = new Set(trades.map((trade: Trade) => trade.ticker));
    return Array.from(tickers).sort();
  }, [trades]);

  const uniqueAlgorithms = useMemo(() => {
    const algorithms = new Set(trades.map((trade: Trade) => trade.algorithm || 'Manual'));
    return Array.from(algorithms).sort();
  }, [trades]);

  // Apply filters (only if not using pre-filtered data from props)
  const filteredTrades = useMemo(() => {
    // If trades came from props (and prop was explicitly passed), they're already filtered by parent component
    // Use them directly without any additional filtering
    if (propFilteredTrades !== undefined) {
      return trades;
    }

    // Otherwise apply internal filters
    let filtered = [...trades];

    // Filter by ticker
    if (filters.ticker) {
      filtered = filtered.filter((trade: Trade) => trade.ticker === filters.ticker);
    }

    // Filter by algorithm
    if (filters.algorithm) {
      filtered = filtered.filter((trade: Trade) => (trade.algorithm || 'Manual') === filters.algorithm);
    }

    // Filter by time range using Pacific timezone for consistency
    if (filters.timeRange !== 'all') {
      // Get today's date key in Pacific timezone
      const todayPacific = new Date().toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
      
      // Calculate cutoff based on Pacific timezone
      const getCutoffDateKey = (): string => {
        const now = new Date();
        const cutoff = new Date(now);
        
        switch (filters.timeRange) {
          case 'today':
            // Today in Pacific
            return todayPacific;
          case 'week':
            cutoff.setDate(cutoff.getDate() - 7);
            break;
          case 'month':
            cutoff.setMonth(cutoff.getMonth() - 1);
            break;
          case 'year':
            cutoff.setFullYear(cutoff.getFullYear() - 1);
            break;
        }
        return cutoff.toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
      };
      
      const cutoffDateKey = getCutoffDateKey();

      filtered = filtered.filter((trade: Trade) => {
        const tradeDate = new Date(trade.executedAt || trade.createdAt);
        const tradeDateKey = tradeDate.toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
        return tradeDateKey >= cutoffDateKey;
      });
    }

    return filtered;
  }, [trades, filters, propFilteredTrades]);

  // Sort trades by date (descending), then order ID (descending), then ticker
  const sortedTrades = useMemo(() => {
    return [...filteredTrades].sort((a, b) => {
      // First sort by date (newest first)
      const dateA = new Date(a.executedAt || a.createdAt).getTime();
      const dateB = new Date(b.executedAt || b.createdAt).getTime();
      if (dateA !== dateB) return dateB - dateA; // Reversed for descending

      // Then sort by order ID (highest first)
      const idA = parseInt(a.snapTradeOrderId || '0');
      const idB = parseInt(b.snapTradeOrderId || '0');
      if (idA !== idB) return idB - idA; // Reversed for descending

      // Finally sort by ticker (alphabetical)
      return a.ticker.localeCompare(b.ticker);
    });
  }, [filteredTrades]);

  // Pagination
  const totalPages = Math.ceil(sortedTrades.length / itemsPerPage);
  const paginatedTrades = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedTrades.slice(startIndex, endIndex);
  }, [sortedTrades, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Format helpers - All dates in Pacific timezone for consistency
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (value?: string | number, decimals: number = 2) => {
    if (!value) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toFixed(decimals);
  };

  const getStatusColor = (status: string, rejected: boolean) => {
    if (rejected) return 'text-red-600 bg-red-100';
    switch (status.toLowerCase()) {
      case 'executed':
      case 'filled':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'cancelled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  // Get emoji based on trade action and status
  const getTradeEmoji = (trade: Trade) => {
    if (trade.rejected || trade.status === 'failed') return '❌';
    if (trade.status === 'pending') return '⏳';
    return trade.action === 'BUY' ? '📈' : '📉';
  };

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (trade: Trade) => {
    if (trade.rejected) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
          Rejected
        </span>
      );
    }

    switch (trade.status.toLowerCase()) {
      case 'executed':
      case 'filled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
            Executed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
            Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
            {trade.status}
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-dark-card shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-dark-card shadow rounded-lg p-6">
        <div className="text-red-600 dark:text-red-400">Error loading trade history</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-card shadow rounded-lg overflow-hidden">
      {/* Header with Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Trade History</h2>

          {/* Filters - only show if not using pre-filtered data */}
          {propFilteredTrades === undefined && (
            <div className="flex flex-wrap gap-3">
              {/* Ticker Filter */}
              <select
                value={filters.ticker}
                onChange={(e) => handleFilterChange('ticker', e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">All Stocks</option>
                {uniqueTickers.map((ticker) => (
                  <option key={ticker} value={ticker}>
                    {ticker}
                  </option>
                ))}
              </select>

              {/* Algorithm Filter */}
              <select
                value={filters.algorithm}
                onChange={(e) => handleFilterChange('algorithm', e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">All Algorithms</option>
                {uniqueAlgorithms.map((algo) => (
                  <option key={algo} value={algo}>
                    {algo}
                  </option>
                ))}
              </select>

              {/* Time Range Filter */}
              <select
                value={filters.timeRange}
                onChange={(e) => handleFilterChange('timeRange', e.target.value as Filters['timeRange'])}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Showing {paginatedTrades.length} of {sortedTrades.length} trades (sorted by Date then Order ID)
          {propFilteredTrades !== undefined && (
            <span className="ml-2 text-blue-600 dark:text-blue-400">
              • Filtered by page-level filters above
            </span>
          )}
        </div>
      </div>

      {/* Mobile View - Simple clickable list */}
      <div className="md:hidden">
        {paginatedTrades.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            No trades found
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {paginatedTrades.map((trade: Trade) => (
              <button
                key={`mobile-${trade.id}`}
                onClick={() => setSelectedTrade(trade)}
                className="w-full flex items-center justify-between px-4 py-4 active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Trade emoji indicator */}
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xl flex-shrink-0">
                    {getTradeEmoji(trade)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-gray-900 dark:text-white">
                        {trade.ticker}
                      </span>
                      <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          trade.action === 'BUY'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        }`}
                      >
                        {trade.action}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {formatNumber(trade.units, 2)} shares • {formatShortDate(trade.executedAt || trade.createdAt)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <div className="text-right">
                    <div className="text-base font-medium text-gray-900 dark:text-white">
                      {trade.total ? `$${formatNumber(trade.total)}` : '-'}
                    </div>
                    {trade.algorithm && (
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        {trade.algorithm}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Trade Detail Bottom Sheet */}
      <BottomSheet
        isOpen={selectedTrade !== null}
        onClose={() => setSelectedTrade(null)}
        title={selectedTrade ? `${selectedTrade.ticker} ${selectedTrade.action}` : undefined}
      >
        {selectedTrade && (
          <div className="space-y-1">
            {/* Status */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              {getStatusBadge(selectedTrade)}
            </div>

            {/* Rejection Message if any */}
            {selectedTrade.rejectionMessage && (
              <div className="py-3 border-b border-gray-100 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 block mb-1">Rejection Reason</span>
                <span className="text-red-600 dark:text-red-400 text-sm">
                  {selectedTrade.rejectionMessage}
                </span>
              </div>
            )}

            {/* Shares */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500 dark:text-gray-400">Shares</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatNumber(selectedTrade.units, 2)}
              </span>
            </div>

            {/* Price */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500 dark:text-gray-400">Price</span>
              <span className="font-medium text-gray-900 dark:text-white">
                ${formatNumber(selectedTrade.executedPrice || selectedTrade.price)}
              </span>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500 dark:text-gray-400">Total</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {selectedTrade.total ? `$${formatNumber(selectedTrade.total)}` : '-'}
              </span>
            </div>

            {/* Commission (if available) */}
            {selectedTrade.commission && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400">Commission</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${formatNumber(selectedTrade.commission)}
                </span>
              </div>
            )}

            {/* Algorithm */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500 dark:text-gray-400">Algorithm</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {selectedTrade.algorithm ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    {selectedTrade.algorithm}
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">Manual</span>
                )}
              </span>
            </div>

            {/* Order ID */}
            {selectedTrade.snapTradeOrderId && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400">Order ID</span>
                <span className="font-medium text-gray-900 dark:text-white text-sm">
                  {selectedTrade.snapTradeOrderId}
                </span>
              </div>
            )}

            {/* Symbol Description */}
            {selectedTrade.symbolDescription && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400">Description</span>
                <span className="font-medium text-gray-900 dark:text-white text-right text-sm max-w-[200px]">
                  {selectedTrade.symbolDescription}
                </span>
              </div>
            )}

            {/* Date/Time */}
            <div className="flex justify-between items-center py-3">
              <span className="text-gray-500 dark:text-gray-400">Date/Time</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatDate(selectedTrade.executedAt || selectedTrade.createdAt)}
              </span>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Desktop View - Full Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Symbol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Shares
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Algorithm
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Order ID
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedTrades.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No trades found
                </td>
              </tr>
            ) : (
              paginatedTrades.map((trade: Trade) => (
                <tr key={trade.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {formatDate(trade.executedAt || trade.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{trade.ticker}</div>
                      {trade.symbolDescription && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{trade.symbolDescription}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        trade.action === 'BUY'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}
                    >
                      {trade.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {formatNumber(trade.units, 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    ${formatNumber(trade.executedPrice || trade.price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {trade.total ? `$${formatNumber(trade.total)}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {trade.algorithm ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {trade.algorithm}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">Manual</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {trade.snapTradeOrderId || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-gray-50 dark:bg-gray-800 px-6 py-3 flex items-center justify-between">
          <div className="flex-1 flex justify-between md:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {/* Page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = index + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNumber
                          ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-300'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}